/**
 * Customer-facing action buttons rendered on the published Summary Report.
 *
 * Renders ONLY when at least one corrective deterministic recommendation has
 * triggered. Aligned with `customer_requests_service.CORRECTIVE_CATEGORIES`
 * on the backend. Two buttons:
 *
 *  1. "Request Instant Proposal"          → POST /api/customer/proposal-requests
 *  2. "Book Free Post Water Test Assessment" → opens modal, POST /api/customer/assessment-requests
 *
 * Wording is intentionally non-guaranteeing: we say "help correct your water
 * supply" / "subject to site conditions, installation requirements, and
 * follow-up testing" — never "guaranteed ADWG compliance".
 */
import { useState } from "react";
import { Send, CalendarCheck2, CheckCircle2, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

// Mirror of backend customer_requests_service.CORRECTIVE_CATEGORIES.
const CORRECTIVE_CATEGORIES = new Set([
  "water_safety",
  "water_quality",
  "filtration",
  "tank_cleaning",
]);

export function hasCorrectiveRecommendations(recommendations) {
  if (!Array.isArray(recommendations)) return false;
  return recommendations.some((r) => CORRECTIVE_CATEGORIES.has((r || {}).category));
}

export function CustomerActionButtons({ testId, recommendations, customerDefaults = {} }) {
  const [showAssessment, setShowAssessment] = useState(false);
  const [proposalSent, setProposalSent] = useState(false);
  const [assessmentSent, setAssessmentSent] = useState(false);
  const [submittingProposal, setSubmittingProposal] = useState(false);

  if (!hasCorrectiveRecommendations(recommendations)) return null;
  if (!testId) return null;

  const submitProposal = async () => {
    if (submittingProposal || proposalSent) return;
    setSubmittingProposal(true);
    try {
      await api.post("/customer/proposal-requests", { test_id: testId });
      setProposalSent(true);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not submit your request. Please try again.");
    } finally {
      setSubmittingProposal(false);
    }
  };

  return (
    <div data-testid="customer-action-buttons" className="mt-6 rounded-2xl border border-cyan-200 bg-white p-5">
      <p data-testid="customer-action-supporting-copy" className="text-sm text-slate-700 leading-relaxed">
        Would you like help correcting the issue? You can request an instant proposal for recommended equipment or services, or book a free post-test assessment with Pristine Water Systems to review the most practical next step.
      </p>

      {/* Confirmation messages take over once submitted (rule 14 — calm, practical). */}
      {proposalSent && (
        <div data-testid="customer-proposal-confirmation" className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
          <span>Thanks — your proposal request has been received. Pristine Water Systems will review your water test results and prepare a practical recommendation for the equipment or services that may help correct your water supply.</span>
        </div>
      )}
      {assessmentSent && (
        <div data-testid="customer-assessment-confirmation" className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
          <span>Thanks — your Free Post Water Test Assessment request has been received. Pristine Water Systems will contact you to confirm a suitable time.</span>
        </div>
      )}

      <div className="mt-4 flex flex-col sm:flex-row gap-2.5">
        <button
          data-testid="customer-request-proposal-btn"
          onClick={submitProposal}
          disabled={submittingProposal || proposalSent}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 hover:bg-cyan-600 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submittingProposal ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> :
            proposalSent ? <><CheckCircle2 className="w-4 h-4" /> Proposal request sent</> :
            <><Send className="w-4 h-4" /> Request Instant Proposal</>}
        </button>
        <button
          data-testid="customer-book-assessment-btn"
          onClick={() => setShowAssessment(true)}
          disabled={assessmentSent}
          className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-white text-slate-900 text-sm font-semibold border border-slate-300 hover:border-cyan-500 hover:text-cyan-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {assessmentSent
            ? <><CheckCircle2 className="w-4 h-4" /> Assessment requested</>
            : <><CalendarCheck2 className="w-4 h-4" /> Book Free Post Water Test Assessment</>}
        </button>
      </div>
      <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
        Subject to site conditions, installation requirements and follow-up testing. Pristine Water Systems is the practical onsite support team for tank cleaning, filtration, UV, pH correction, BioCleanse, equipment supply and installation.
      </p>

      {showAssessment && (
        <AssessmentModal
          testId={testId}
          defaults={customerDefaults}
          onClose={() => setShowAssessment(false)}
          onSubmitted={() => { setAssessmentSent(true); setShowAssessment(false); }}
        />
      )}
    </div>
  );
}


function AssessmentModal({ testId, defaults, onClose, onSubmitted }) {
  const [form, setForm] = useState({
    name: defaults.name || "",
    phone: defaults.phone || "",
    email: defaults.email || "",
    address: defaults.address || "",
    preferred_datetime: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  const valid = form.name.trim() && form.phone.trim() && form.email.trim() && form.address.trim();

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try {
      await api.post("/customer/assessment-requests", { test_id: testId, ...form });
      onSubmitted();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="assessment-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between p-6 pb-3 border-b border-slate-100">
          <div>
            <h3 className="font-display text-lg font-semibold text-slate-900">Book a Free Post Water Test Assessment</h3>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">Pristine Water Systems will contact you to confirm a suitable time and review the most practical next step for your water supply.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="assessment-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-3">
          <Field id="name" label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
          <Field id="phone" label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required />
          <Field id="email" label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
          <Field id="address" label="Property address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} required />
          <Field id="preferred_datetime" label="Preferred day / time" value={form.preferred_datetime} onChange={(v) => setForm({ ...form, preferred_datetime: v })} placeholder="e.g. Wed afternoon" />
          <div>
            <label className="text-xs uppercase tracking-wider font-semibold text-slate-700">Notes</label>
            <textarea
              data-testid="assessment-field-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              placeholder="Anything you'd like Pristine to know before they call?"
              className="mt-1.5 w-full px-3 py-2 rounded-xl border border-slate-300 text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
            />
          </div>
        </div>
        <div className="p-6 pt-3 border-t border-slate-100 flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm font-semibold text-slate-600 hover:bg-slate-100" data-testid="assessment-modal-cancel">
            Cancel
          </button>
          <button
            data-testid="assessment-modal-submit"
            onClick={submit}
            disabled={!valid || saving}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 hover:bg-cyan-600 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Send assessment request</>}
          </button>
        </div>
      </div>
    </div>
  );
}


function Field({ id, label, value, onChange, type = "text", required = false, placeholder = "" }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider font-semibold text-slate-700">
        {label}{required && <span className="text-rose-500 ml-1">*</span>}
      </label>
      <input
        data-testid={`assessment-field-${id}`}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full px-3 py-2 rounded-xl border border-slate-300 text-sm focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
      />
    </div>
  );
}
